// src/lib/goldenHourData.js — ROUND 2 version. Replaces the Round 1 file completely.
// Same exports as Round 1 (COLOUR_HEX, FOCAL, SHORT, toCardItem, useCatalog, usePublicSettings) plus
// useProductDetail, useCartActions, checkPin, useShopMemory. Written against this app's real lib/api, lib/cart, lib/utils.
import { useEffect, useState } from "react";
import { http, IMG_URL } from "./api";
import { useCart } from "./cart";
import { hasPlaceholder, waNumber } from "./utils";

export const COLOUR_HEX = {
  "Warm Ivory": "#EFE6D6", "Sage Green": "#9CAF88", "Dusty Rose": "#C9A3A0", "Charcoal Black": "#2F2E2C", "Champagne Gold": "#D6C18E",
  Ivory: "#F1EAD9", "Rose Pink": "#E3A6AE", Gold: "#C9A44C",
  "Metallic Silver": "#BFC1C4", "Metallic Copper": "#B5704A", "Metallic Bronze": "#8C6A3F",
  "Antique Silver": "#A8A39A", Bronze: "#8C6A3F", Copper: "#B87333", Terracotta: "#C26A45",
  // legacy names kept
  "Marigold Gold": "#D99A1E", "Ruby Red": "#A3182B", "Royal Blue": "#23409A", "Emerald Green": "#1F6B4F",
  "Magenta Purple": "#7B2D6E", "Plain White": "#F4F0E8", Cream: "#EFE6D6", Sage: "#97A38A",
  Charcoal: "#3A3835", Marigold: "#E1A22A", Blush: "#E8C6BB",
};
// Keyword fallback so a colour name we haven't mapped still gets a sensible dot (grey only if nothing matches).
const COLOUR_GUESS = [
  [/rose|pink|blush/i, "#E3A6AE"], [/sage|olive|emerald|green/i, "#9CAF88"],
  [/gold|champagne|marigold|brass/i, "#C9A44C"], [/silver|steel|grey|gray/i, "#BFC1C4"],
  [/copper/i, "#B87333"], [/bronze/i, "#8C6A3F"], [/black|charcoal|noir/i, "#2F2E2C"],
  [/ivory|white|cream|bone/i, "#EFE6D6"], [/terracotta|terra|clay|rust/i, "#C26A45"],
  [/navy|royal|blue/i, "#23409A"], [/ruby|crimson|red/i, "#A3182B"], [/magenta|violet|purple/i, "#7B2D6E"],
];
export function colourHex(name) {
  if (!name) return "#B9B5AE";
  if (COLOUR_HEX[name]) return COLOUR_HEX[name];
  const hit = COLOUR_GUESS.find(([re]) => re.test(name));
  return hit ? hit[1] : "#B9B5AE";
}
export const FOCAL = {
  "LO-T01": "50% 62%", "LO-I01": "56% 34%", "LO-C03": "52% 46%", "LO-C07": "52% 48%", "LO-C01": "54% 56%",
  "LO-P11": "38% 58%", "LO-K01": "55% 58%", "LO-T04": "50% 56%", "LO-T12": "46% 56%", "LO-C17": "50% 58%",
  "LO-C02": "40% 60%", "LO-P12": "55% 56%", "LO-G07": "50% 55%", "LO-G02": "45% 52%", "LO-G03": "50% 55%", "LO-G01": "50% 50%",
};
export const SHORT = {
  "LO-T01": "Lotus Glow", "LO-T04": "Heart Holder", "LO-T12": "You & Me Holder", "LO-G02": "Diwali Glow Box",
  "LO-G07": "Grand Oak Hamper", "LO-G03": "Housewarming Box", "LO-G01": "Glow Trio Box", "LO-P12": "Log Planter",
};

const clean = (s) => (typeof s === "string" && !hasPlaceholder(s) ? s : "");
// "a · b [confirm] · c" -> "a · c": drop only the segments that hold a placeholder
const cleanList = (s) => String(s || "").split(" · ").map((x) => x.trim()).filter((x) => x && !hasPlaceholder(x)).join(" · ");
const contentsOf = (p) => cleanList(p.in_box).split(" · ")[0].split(/\s\+\s/).map((x) => x.trim()).filter(Boolean);
const isRealColour = (c) => typeof c !== "string" || !/^choose/i.test(c);

export function toCardItem(p, worthBySku = {}) {
  const cs = p.colour_shown;
  const img = (cs && p.images_by_colour && p.images_by_colour[cs] && p.images_by_colour[cs][0]) || (p.images || [])[0];
  const isBox = !!p.is_bundle || p.category === "Gift Boxes" || String(p.sku).startsWith("LO-G");
  const colours = isBox ? [] : (p.colours || p.colour_options || []).filter(isRealColour)
    .map((c) => (typeof c === "string" ? { name: c, hex: colourHex(c) } : { ...c, hex: c.hex || colourHex(c.name) }));
  const price = p.price != null ? p.price : p.price_inr;
  const compareAt = p.compareAt != null ? p.compareAt : (p.compare_at_inr || 0);
  const worth = p.bundle_worth || worthBySku[p.sku] || p.worth_if_separate || p.worth || 0;
  return {
    sku: p.sku, to: `/product/${p.sku}`, name: p.name, short: p.short_name || SHORT[p.sku] || p.name,
    card_line: clean(p.card_line),
    price, compareAt, image: img ? `${IMG_URL(img)}?size=card` : null, imageLarge: img ? `${IMG_URL(img)}?size=zoom` : null,
    focal: p.focal || FOCAL[p.sku] || "50% 50%", badge: p.badge && p.badge !== "none" ? p.badge : null, colours,
    isBox, worth, pieces: isBox ? contentsOf(p).length : 0, contents: isBox ? contentsOf(p) : [],
    perPiece: p.pieces_in_pack > 1 ? `· ₹${Math.round(price / p.pieces_in_pack)} each` : "",
    hasFilm: !!p.film_url, film: p.film_url || null, alt: `${p.name}${p.colour_shown ? `, ${p.colour_shown}` : ""}`,
    occasions: p.occasions || [], type: p.category || "", pin_rank: p.pin_rank || 100, created_at: p.created_at || "",
    colourShown: p.colour_shown || null,
  };
}

// Catalogue: products + gift-box worth (bundle_worth lives on /catalog/gift-boxes), fetched once per page load.
let cache = null; let inflight = null;
const loadCatalog = () => {
  inflight = inflight || Promise.all([
    http.get("/catalog/products?limit=200"),
    http.get("/catalog/gift-boxes").catch(() => ({ data: [] })),
  ]).then(([a, b]) => {
    const worth = Object.fromEntries((b.data.items || b.data || []).map((x) => [x.sku, x.bundle_worth || 0]));
    return (a.data.items || a.data).map((p) => toCardItem(p, worth));
  });
  return inflight;
};
export function useCatalog() {
  const [items, setItems] = useState(cache || []);
  const [loading, setLoading] = useState(!cache);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (cache) { setItems(cache); setLoading(false); return undefined; }
    let on = true;
    setLoading(true); setError(false);
    loadCatalog().then((x) => { cache = x; if (on) { setItems(x); setLoading(false); } })
      .catch(() => { inflight = null; if (on) { setError(true); setLoading(false); } });
    return () => { on = false; };
  }, [reload]);
  return { items, bySku: (sku) => items.find((i) => i.sku === sku), loading, error, retry: () => { cache = null; inflight = null; setReload((n) => n + 1); } };
}

// Public settings, fetched once and shared (Round 1 fetched it once per component).
let sCache = null; let sInflight = null;
export function usePublicSettings() {
  const [s, setS] = useState(sCache || {});
  useEffect(() => {
    if (sCache) return undefined;
    let on = true;
    sInflight = sInflight || http.get("/settings/public").then((r) => r.data || {});
    sInflight.then((d) => { sCache = d; if (on) setS(d); }).catch(() => { sInflight = null; });
    return () => { on = false; };
  }, []);
  return s;
}

const imgs = (ids) => (ids || []).map((id) => ({ web: IMG_URL(id), zoom: `${IMG_URL(id)}?size=zoom` }));
const cmFrom = (dims) => { const m = String(dims || "").match(/([\d.]+)\s*cm/i); return m ? parseFloat(m[1]) : null; };

// default colour for a box piece = the colour that piece is photographed in (so the box arrives as pictured)
const pieceDefault = (c, shownBySku) => {
  const opts = (c.colour_options || []).filter(isRealColour);
  const shown = shownBySku[c.sku];
  return shown && opts.includes(shown) ? shown : opts[0] || "";
};
const shownMap = (items) => Object.fromEntries((items || []).map((i) => [i.sku, i.colourShown]));

function toDetail(raw, s, shownBySku = {}) {
  const card = toCardItem(raw);
  const isBox = (raw.components || []).length > 0 || card.isBox;
  const options = (raw.colour_options || []).filter(isRealColour);
  const diwali = (s.festive_cutoffs || []).find((c) => c.name === "Diwali");
  const festiveLive = diwali && new Date(`${diwali.cutoff_date}T23:59:59`) >= new Date();
  const wa = waNumber(s.whatsapp_number);
  const care = (s.consumer_care_phone || s.consumer_care_email) ? [s.consumer_care_phone, s.consumer_care_email, s.consumer_care_hours].filter(Boolean).join(" · ") : "";
  return {
    ...card, isBox,
    gallery: imgs(raw.images),
    imagesByColour: Object.fromEntries(Object.entries(raw.images_by_colour || {}).map(([k, v]) => [k, imgs(v)])),
    description: clean(raw.description),
    reasons: (raw.reasons || []).filter((r) => !hasPlaceholder(r) && !(!isBox && /colour of every piece/i.test(r))),
    in_box: cleanList(raw.in_box),
    faq: (raw.faq || []).filter((f) => !hasPlaceholder(f.q) && !hasPlaceholder(f.a)),
    multi_buy_line: clean(raw.multi_buy_line),
    pairs_with_box: raw.pairs_with_box || null,
    colours: isBox ? [] : card.colours,
    // default to the colour in the photo, so the picture and the selection agree
    colourDefault: raw.colour_shown && options.includes(raw.colour_shown) ? raw.colour_shown : options[0] || null,
    colour_shown: raw.colour_shown || null,
    scaleCm: cmFrom(clean(raw.dimensions)),
    readyStock: !!raw.ready_stock,
    festiveLine: festiveLive ? `Order by ${s.retail_cutoff_label || "1 Nov"} for Diwali delivery` : "",
    whatsappHref: wa ? `https://wa.me/${wa}?text=${encodeURIComponent(`Hi, a question about ${raw.name}`)}` : null,
    boxPieces: (raw.components || []).map((c, i) => ({ key: `${c.sku}-${i}`, sku: c.sku, name: c.name, colours: (c.colour_options || []).filter(isRealColour), default: pieceDefault(c, shownBySku) })),
    legal: {
      generic_name: clean(raw.generic_name),
      manufacturer: clean(s.legal_business_name) ? [s.legal_business_name, clean(s.business_address)].filter(Boolean).join(", ") : "",
      country_of_origin: clean(raw.country_of_origin) || "India",
      net_quantity: clean(raw.net_quantity), dimensions: clean(raw.dimensions), material: clean(raw.material),
      month_of_manufacture: "Printed on the pack (month and year of pour)",
      consumer_care: hasPlaceholder(care) ? "" : care,
    },
    shipping: { freeOver: s.free_shipping_threshold || 699, flat: s.flat_shipping_fee || 0, dispatch: clean(s.dispatch_text) },
    seo: { title: raw.seo_title || `${raw.name} | Little Oak`, description: raw.seo_description || "" },
  };
}

// Product page data: same endpoints as the old ProductDetail (/catalog/products/:sku, /reviews/product/:sku).
export function useProductDetail(sku) {
  const s = usePublicSettings();
  const { items } = useCatalog();
  const [st, setSt] = useState({ loading: true, raw: null, reviews: [] });
  useEffect(() => {
    let on = true;
    setSt({ loading: true, raw: null, reviews: [] });
    http.get(`/catalog/products/${sku}`)
      .then((r) => on && setSt((x) => ({ ...x, loading: false, raw: r.data })))
      .catch(() => on && setSt((x) => ({ ...x, loading: false, raw: null })));
    http.get(`/reviews/product/${sku}`)
      .then((r) => on && setSt((x) => ({ ...x, reviews: (r.data || []).filter((v) => !v.is_sample) })))
      .catch(() => {});
    return () => { on = false; };
  }, [sku]);
  return { loading: st.loading, p: st.raw ? toDetail(st.raw, s, shownMap(items)) : null, reviews: st.reviews, raw: st.raw };
}

// Cart actions with the SAME line shape the old ProductDetail sent to addLine().
export function useCartActions() {
  const { addLine, setAddon, track } = useCart();
  return {
    add: ({ sku, colour, boxPieces, boxColours, qty = 1, giftWrap = true, note = "", recipient = null, extras = {} }) => {
      if (extras.giftBag) setAddon("add_gift_bag", true);
      if (extras.nameTag) setAddon("add_name_tag", true);
      addLine({
        sku, colour: colour || "", qty, note, gift_wrap: giftWrap,
        component_colours: boxPieces && boxPieces.length
          ? boxPieces.map((pc) => ({ sku: pc.sku, name: pc.name, colour: (boxColours && boxColours[pc.key]) || pc.default || pc.colours[0] || "" }))
          : undefined,
        recipient_name: recipient ? recipient.name : "",
        recipient_phone: recipient ? recipient.phone : "",
      });
    },
    track,
  };
}

// Delivery estimate: the old ProductDetail rule (metro 4 days, else 6, +2 if made to order).
const METRO = ["mumbai", "delhi", "bengaluru", "bangalore", "chennai", "kolkata", "hyderabad", "pune", "ahmedabad", "gurgaon", "gurugram", "noida"];
export async function checkPin(pin, p) {
  let days = 6; let place = "";
  try {
    const r = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const j = await r.json();
    if (j && j[0] && j[0].Status === "Error") return { ok: false, message: "We couldn't find that PIN code. Check it and try again." };
    const po = j && j[0] && j[0].PostOffice && j[0].PostOffice[0];
    const city = ((po && (po.District || po.Region)) || "").toLowerCase();
    place = po ? po.District : "";
    if (METRO.some((m) => city.includes(m))) days = 4;
  } catch (e) { /* estimate without the lookup */ }
  if (p && !p.readyStock) days += 2;
  const dt = new Date(); dt.setDate(dt.getDate() + days);
  const when = dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
  return { ok: true, message: `Arrives by ${when}${place ? ` in ${place}` : ""}.` };
}

// Shop: remember "Show more" count + scroll when opening a product, restore on browser Back (old key: "shop_state").
export function useShopMemory(key, ready) {
  const [shown, setShown] = useState(12);
  useEffect(() => {
    if (!ready) return;
    try {
      const st = JSON.parse(sessionStorage.getItem("shop_state") || "null");
      sessionStorage.removeItem("shop_state");
      const curIdx = window.history.state && window.history.state.idx;
      const fresh = st && Date.now() - (st.ts || 0) < 10000;
      if (st && st.key === key && (st.idx === curIdx || fresh)) {
        if (st.visible) setShown(st.visible);
        const y = st.scroll || 0;
        // restore after the grid (with its restored card count) has laid out
        [60, 180, 360].forEach((d) => setTimeout(() => window.scrollTo(0, y), d));
      }
    } catch (e) { /* ignore */ }
  }, [ready, key]);
  const remember = () => {
    try { sessionStorage.setItem("shop_state", JSON.stringify({ key, visible: shown, scroll: window.scrollY, idx: window.history.state && window.history.state.idx, ts: Date.now() })); } catch (e) { /* ignore */ }
  };
  return { shown, setShown, remember };
}

// "Add to basket" for a gift box from Home / Gift boxes: same line as its product page with default piece colours.
export function useAddBox() {
  const { addLine } = useCart();
  return async (item) => {
    let comps = [];
    try {
      const r = await http.get(`/catalog/products/${item.sku}`);
      const shown = shownMap(cache || []);
      comps = (r.data.components || []).map((c) => ({ sku: c.sku, name: c.name, colour: pieceDefault(c, shown) }));
    } catch (e) { /* add without piece colours; the order screen shows them as not chosen */ }
    addLine({ sku: item.sku, colour: "", qty: 1, note: "", component_colours: comps.length ? comps : undefined, recipient_name: "", recipient_phone: "" });
  };
}

// Festive switches, from settings.festive_cutoffs (the "Diwali" row and the "bulk/corporate" row).
// diwaliOn: until Diwali itself (delivery_by); retailOn / bulkOn: until each order-by date. Sections hide themselves after.
export function useSeason() {
  const s = usePublicSettings();
  const list = s.festive_cutoffs || [];
  const now = new Date();
  const end = (d) => (d ? new Date(`${d}T23:59:59`) : null);
  const diwali = list.find((x) => x.name === "Diwali");
  const bulk = list.find((x) => /bulk|corporate/i.test(x.name || ""));
  const diwaliEnd = diwali ? end(diwali.delivery_by || diwali.cutoff_date) : null;
  return {
    diwaliOn: !diwaliEnd || diwaliEnd >= now,
    retailOn: !!(diwali && end(diwali.cutoff_date) >= now),
    bulkOn: !!(bulk && end(bulk.cutoff_date) >= now),
    retailLabel: s.retail_cutoff_label || "1 Nov",
    bulkLabel: s.bulk_cutoff_label || "20 Oct",
  };
}
